/**
 * Meetings, occurrences, notes, items, recordings, summaries, and virtual account summary
 * (TMMAPP-0014). Wire types from `src/api/generated/`; every response is normalized.
 *
 * A meeting lives on a virtual account or stands alone (TMMAPP-0159 §D, MM-D-150). Every call that
 * names one meeting takes its **home**: the virtual account's id, or `null` for a standalone meeting,
 * whose routes are `/v1/me/meetings/{meetingId}/…`. The two families never cross (each answers 404
 * for the other kind), so the home always comes from the served row, never from a guess.
 */
import { download, request, requestMeta, type DownloadResult } from './client';
import { personNameOrNull } from './personNames';
import { normalizeAiJobFailureReason, type AiJobFailureReason } from './aiJobFailure';
import { ApiShapeError } from './errors';
import type {
  VirtualAccountRegisterLine as WireVirtualAccountRegisterLine,
  VirtualAccountSummaryView as WireVirtualAccountSummary,
  VirtualAccountSummaryPutRequest,
  MeetingCalendar as WireCalendar,
  MeetingCalendarEntry as WireCalendarEntry,
  MeetingCreateRequest,
  MeetingItemCreateRequest,
  MeetingItemPatchRequest,
  MeetingInviteView as WireInvite,
  MeetingItemVersionView as WireItemVersion,
  MeetingItemView as WireItem,
  MeetingNoteView as WireNote,
  MeetingOccurrenceResponse as WireOccurrenceResponse,
  MeetingOccurrenceView as WireOccurrence,
  MeetingPatchRequest,
  MeetingPerson as WirePerson,
  MeetingRecordingView as WireRecording,
  MeetingResponse as WireMeeting,
  MeetingSummaryView as WireSummary,
  MeetingWorkspace as WireWorkspace,
  MyMeetingCreateRequest,
  Recurrence as WireRecurrence,
  StandaloneItemView as WireStandaloneItem,
  StandaloneOccurrenceResponse as WireStandaloneOccurrenceResponse,
  StandaloneSummaryView as WireStandaloneSummary,
} from './generated';
import { normalizeRecurrence, type RecurrenceRule } from './planned';
import { normalizeCashflowPlan, type CashflowPlan } from './projections';
import { normalizeEnvelope, type Envelope } from './envelopes';

export interface VirtualAccountRegisterAllocation {
  envelopeId: number | null;
  envelopeName: string | null;
  amountMinor: number;
}

export interface VirtualAccountRegisterLine {
  id: number;
  postedOn: string;
  payee: string;
  publishedMinor: number;
  allocations: VirtualAccountRegisterAllocation[];
}

export type MeetingStatus = 'SCHEDULED' | 'CANCELLED';
export type OccurrenceStatus = 'SCHEDULED' | 'HELD' | 'SKIPPED';
export type InviteStatus = 'PENDING' | 'SENT' | 'FAILED' | 'CANCELLED';
export type ItemKind = 'DECISION' | 'COMMITMENT' | 'GOAL' | 'QUESTION';
export type ItemStatus =
  'AGREED' | 'REVISED' | 'RETIRED' | 'OPEN' | 'DONE' | 'DROPPED' | 'MET' | 'ANSWERED';
export type ItemOrigin = 'MANUAL' | 'AI';
export type SummaryKind = 'THIS_MEETING' | 'ROLLING';
export type SummaryStatus = 'PENDING' | 'READY' | 'FAILED';
export type RecordingKind = 'URL' | 'TRANSCRIPT_TEXT';
/** Where a meeting lives (MM-D-150): on a virtual account, or standalone (talk only). */
export type MeetingKind = 'VIRTUAL_ACCOUNT' | 'STANDALONE';
/** A meeting's home in the API: its virtual account's id, or `null` for a standalone meeting. */
export type MeetingHome = number | null;

export const ITEM_STATUSES: Record<ItemKind, readonly ItemStatus[]> = {
  DECISION: ['AGREED', 'REVISED', 'RETIRED'],
  COMMITMENT: ['OPEN', 'DONE', 'DROPPED'],
  GOAL: ['OPEN', 'MET', 'RETIRED'],
  QUESTION: ['OPEN', 'ANSWERED'],
};

export interface MeetingPerson {
  /**
   * Null on a standalone meeting for someone outside the reader's sharing pool (TMMAPI-0188 G11):
   * they read as "A member" and are never addressable.
   */
  userId: number | null;
  displayName: string;
}

/** Someone the caller may invite to a standalone meeting (`GET /v1/me/meeting-people`). */
export interface PoolPerson {
  /** Only ever sent back as `inviteeUserIds`; never rendered. */
  userId: number;
  displayName: string;
}

export interface MeetingInvite {
  userId: number | null;
  displayName: string;
  status: InviteStatus | null;
  lastSentAt: string | null;
}

export interface Meeting {
  id: number;
  kind: MeetingKind;
  /** Null on a standalone meeting. */
  virtualAccountId: number | null;
  title: string;
  startsAt: string;
  durationMinutes: number;
  seriesTimezone: string;
  recurrence: RecurrenceRule | null;
  organizer: MeetingPerson;
  invitees: MeetingInvite[];
  meetingUrl: string | null;
  locationText: string | null;
  description: string | null;
  reminderLeadMinutes: number | null;
  status: MeetingStatus;
  nextOccurrenceAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingCalendarEntry {
  meetingId: number;
  occurrenceId: number | null;
  occurrenceKey: string;
  kind: MeetingKind;
  /** Null on a standalone meeting. */
  virtualAccountId: number | null;
  title: string;
  startsAt: string;
  endsAt: string;
  status: OccurrenceStatus;
  live: boolean;
  organizerDisplayName: string | null;
  invitees: MeetingPerson[];
  meetingUrl: string | null;
  locationText: string | null;
  seriesTimezone: string | null;
  recurrence: RecurrenceRule | null;
  latestNoteExcerpt: string | null;
}

export interface MeetingCalendar {
  from: string;
  to: string;
  items: MeetingCalendarEntry[];
}

export interface MeetingOccurrence {
  meetingId: number;
  /** Null on a standalone meeting. */
  virtualAccountId: number | null;
  occurrenceId: number | null;
  occurrenceKey: string;
  startsAt: string;
  endsAt: string;
  status: OccurrenceStatus;
  heldStartedAt: string | null;
  heldEndedAt: string | null;
  priorOccurrenceId: number | null;
}

export interface MeetingSinceLast {
  since: string | null;
  priorOccurrenceId: number | null;
  register: VirtualAccountRegisterLine[];
  nextCursor: string | null;
}

export interface VirtualAccountStandingSummary {
  body: string;
  authorDisplayName: string | null;
  /** Null only on the empty summary a 404 stands for (none written yet). */
  createdAt: string | null;
  /** Null only on the empty summary a 404 stands for. */
  version: number | null;
}

export interface MeetingNote {
  id: number;
  /** Null on a standalone meeting for an author outside the reader's pool. */
  authorUserId: number | null;
  authorDisplayName: string | null;
  body: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  occurrenceId: number;
}

export interface MeetingItem {
  id: number;
  /** Null on a standalone meeting's item: it carries no envelope, target or progress either. */
  virtualAccountId: number | null;
  kind: ItemKind;
  body: string;
  status: ItemStatus | null;
  origin: ItemOrigin | null;
  pendingConfirmation: boolean;
  ownerUserId: number | null;
  ownerDisplayName: string | null;
  dueOn: string | null;
  targetMinor: number | null;
  envelopeId: number | null;
  /** The item's envelope's name (TMMAPI-0186 U3), served exactly when `envelopeId` is. */
  envelopeName: string | null;
  /** The item's virtual account's name (TMMAPI-0186 U3). */
  virtualAccountName: string | null;
  /**
   * Who made the current version (TMMAPI-0186 U3), a name only; null when they have none and on a
   * pending AI item.
   */
  lastActorDisplayName: string | null;
  /**
   * A GOAL with an envelope: the envelope's balance in published terms (TMMAPI-0186 U3), in the
   * virtual account's currency; null otherwise.
   */
  progressMinor: number | null;
  createdInOccurrenceId: number;
  meetingId: number;
  occurrenceKey: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingItemVersion {
  version: number;
  actorUserId: number | null;
  body: string;
  status: ItemStatus | null;
  reason: string;
  dueOn: string | null;
  ownerUserId: number | null;
  createdAt: string;
  /** Served display name; null when omitted (the row renders "Someone"). */
  actorDisplayName: string | null;
}

export interface MeetingRecording {
  id: number;
  kind: RecordingKind;
  url: string | null;
  transcript: string | null;
  addedByUserId: number | null;
  createdAt: string;
  occurrenceId: number;
}

export interface MeetingSummary {
  id: number;
  kind: SummaryKind | null;
  status: SummaryStatus;
  body: string | null;
  /** Null only on the placeholder a `202` with no body becomes (`requestSummary`). */
  createdAt: string | null;
  /** Null only on the placeholder a `202` with no body becomes (`requestSummary`). */
  occurrenceId: number | null;
  proposedItems: MeetingItem[];
  /** Why a FAILED summary failed (TMMAPI-0152); null otherwise and on older ones. */
  failureReason: AiJobFailureReason | null;
  /** Served only to the person whose wallet was refused, while their first purchase is due. */
  firstPurchaseRequired: boolean;
  /**
   * The wallet's debt, only when `failureReason` is `AI_DEBT` and only to the owner (TMMAPI-0162
   * U6). Null when not served.
   */
  debtMinor: number | null;
}

/** A virtual account meeting's money sections. A standalone meeting has none (talk only). */
export interface MeetingMoney {
  /** The virtual account's Unassigned (TMMAPI-0145, MM-D-112): the hero figure; may be negative. */
  unassignedMinor: number;
  /** The virtual account's headline balance. */
  balanceMinor: number;
  envelopes: Envelope[];
  sinceLastMeeting: MeetingSinceLast;
  lookingAhead: CashflowPlan | null;
  summary: VirtualAccountStandingSummary | null;
}

export interface MeetingWorkspace {
  /** Null on a standalone meeting: its workspace serves no money section at all (MM-D-150). */
  money: MeetingMoney | null;
  /** A standalone meeting's agenda (its description); null when blank and on a virtual account's. */
  agenda: string | null;
  notes: MeetingNote[];
  items: MeetingItem[];
}

export interface MeetingOccurrenceDetail {
  kind: MeetingKind;
  occurrence: MeetingOccurrence;
  workspace: MeetingWorkspace;
}

const INVITE_STATUSES: ReadonlySet<string> = new Set(['PENDING', 'SENT', 'FAILED', 'CANCELLED']);
const ITEM_KINDS: ReadonlySet<string> = new Set(['DECISION', 'COMMITMENT', 'GOAL', 'QUESTION']);
const ITEM_STATUS_SET: ReadonlySet<string> = new Set([
  'AGREED',
  'REVISED',
  'RETIRED',
  'OPEN',
  'DONE',
  'DROPPED',
  'MET',
  'ANSWERED',
]);
const SUMMARY_KINDS: ReadonlySet<string> = new Set(['THIS_MEETING', 'ROLLING']);
const SUMMARY_STATUSES: ReadonlySet<string> = new Set(['PENDING', 'READY', 'FAILED']);
const MEETING_STATUSES: ReadonlySet<string> = new Set(['SCHEDULED', 'CANCELLED']);
const OCC_STATUSES: ReadonlySet<string> = new Set(['SCHEDULED', 'HELD', 'SKIPPED']);

/** The served kind; one this app doesn't know reads from whether a virtual account is named. */
function kindOf(kind: string | undefined, virtualAccountId: number | undefined): MeetingKind {
  if (kind === 'STANDALONE' || kind === 'VIRTUAL_ACCOUNT') return kind;
  return virtualAccountId === undefined ? 'STANDALONE' : 'VIRTUAL_ACCOUNT';
}

/** An unknown occurrence status (one added to the contract later) reads as SCHEDULED. */
const occurrenceStatus = (v: string): OccurrenceStatus =>
  OCC_STATUSES.has(v) ? (v as OccurrenceStatus) : 'SCHEDULED';
const itemStatus = (v: string): ItemStatus | null =>
  ITEM_STATUS_SET.has(v) ? (v as ItemStatus) : null;

function personOf(w: WirePerson): MeetingPerson {
  return { userId: w.userId ?? null, displayName: w.displayName ?? '' };
}

function inviteOf(w: WireInvite): MeetingInvite {
  return {
    userId: w.userId ?? null,
    displayName: w.displayName ?? '',
    status: INVITE_STATUSES.has(w.status) ? w.status : null,
    lastSentAt: w.lastSentAt ?? null,
  };
}

export function recurrenceToWire(rule: RecurrenceRule): WireRecurrence {
  const wire: WireRecurrence = { frequency: rule.frequency, startOn: rule.startOn };
  if (rule.interval !== null) wire.interval = rule.interval;
  if (rule.byWeekdays !== null) wire.byWeekdays = [...rule.byWeekdays];
  if (rule.byMonthDays !== null) wire.byMonthDays = [...rule.byMonthDays];
  if (rule.byWeekOrdinal !== null) wire.byWeekOrdinal = rule.byWeekOrdinal;
  if (rule.byOrdinalWeekday !== null) wire.byOrdinalWeekday = rule.byOrdinalWeekday;
  if (rule.byMonth !== null) wire.byMonth = rule.byMonth;
  if (rule.endsOn !== null) wire.endsOn = rule.endsOn;
  if (rule.occurrenceCount !== null) wire.occurrenceCount = rule.occurrenceCount;
  return wire;
}

function recurrenceOf(wire: WireRecurrence | undefined): RecurrenceRule | null {
  return wire ? normalizeRecurrence(wire) : null;
}

export function normalizeMeeting(w: WireMeeting): Meeting {
  return {
    id: w.id,
    kind: kindOf(w.kind, w.virtualAccountId),
    virtualAccountId: w.virtualAccountId ?? null,
    title: w.title,
    startsAt: w.startsAt,
    durationMinutes: w.durationMinutes,
    seriesTimezone: w.seriesTimezone,
    recurrence: recurrenceOf(w.recurrence),
    organizer: personOf(w.organizer),
    invitees: w.invitees.map(inviteOf),
    meetingUrl: w.meetingUrl ?? null,
    locationText: w.locationText ?? null,
    description: w.description ?? null,
    reminderLeadMinutes: w.reminderLeadMinutes ?? null,
    status: MEETING_STATUSES.has(w.status) ? w.status : 'SCHEDULED',
    nextOccurrenceAt: w.nextOccurrenceAt ?? null,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

export function normalizeCalendarEntry(w: WireCalendarEntry): MeetingCalendarEntry {
  return {
    meetingId: w.meetingId,
    occurrenceId: w.occurrenceId ?? null,
    occurrenceKey: w.occurrenceKey,
    kind: kindOf(w.kind, w.virtualAccountId),
    virtualAccountId: w.virtualAccountId ?? null,
    title: w.title,
    startsAt: w.startsAt,
    endsAt: w.endsAt,
    status: occurrenceStatus(w.status),
    live: w.live,
    organizerDisplayName: w.organizerDisplayName ?? null,
    invitees: w.invitees.map(personOf),
    meetingUrl: w.meetingUrl ?? null,
    locationText: w.locationText ?? null,
    seriesTimezone: w.seriesTimezone ?? null,
    recurrence: recurrenceOf(w.recurrence),
    latestNoteExcerpt: w.latestNoteExcerpt ?? null,
  };
}

export function normalizeCalendar(w: WireCalendar): MeetingCalendar {
  return { from: w.from, to: w.to, items: w.items.map(normalizeCalendarEntry) };
}

export function normalizeNote(w: WireNote): MeetingNote {
  return {
    id: w.id,
    authorUserId: w.authorUserId ?? null,
    authorDisplayName: w.authorDisplayName ?? null,
    body: w.body,
    version: w.version,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
    occurrenceId: w.occurrenceId,
  };
}

/** An item; `null` (dropped) for a kind this app does not know. */
export function normalizeItem(w: WireItem): MeetingItem | null {
  if (!ITEM_KINDS.has(w.kind)) return null;
  return {
    id: w.id,
    virtualAccountId: w.virtualAccountId ?? null,
    kind: w.kind,
    body: w.body,
    status: itemStatus(w.status),
    origin: w.origin === 'AI' || w.origin === 'MANUAL' ? w.origin : null,
    pendingConfirmation: w.pendingConfirmation,
    ownerUserId: w.ownerUserId ?? null,
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
    dueOn: w.dueOn ?? null,
    targetMinor: w.targetMinor ?? null,
    envelopeId: w.envelopeId ?? null,
    envelopeName: w.envelopeName ?? null,
    virtualAccountName: w.virtualAccountName ?? null,
    lastActorDisplayName: personNameOrNull(w.lastActorDisplayName),
    progressMinor: w.progressMinor ?? null,
    createdInOccurrenceId: w.createdInOccurrenceId,
    meetingId: w.meetingId,
    occurrenceKey: w.occurrenceKey,
    version: w.version,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

/**
 * A standalone meeting's item (TMMAPI-0188): words only, so no virtual account, envelope, target or
 * progress. `null` (dropped) for a kind this app does not know.
 */
export function normalizeStandaloneItem(w: WireStandaloneItem): MeetingItem | null {
  if (!ITEM_KINDS.has(w.kind)) return null;
  return {
    id: w.id,
    virtualAccountId: null,
    kind: w.kind,
    body: w.body,
    status: itemStatus(w.status),
    origin: w.origin === 'AI' || w.origin === 'MANUAL' ? w.origin : null,
    pendingConfirmation: w.pendingConfirmation,
    ownerUserId: w.ownerUserId ?? null,
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
    dueOn: w.dueOn ?? null,
    targetMinor: null,
    envelopeId: null,
    envelopeName: null,
    virtualAccountName: null,
    lastActorDisplayName: personNameOrNull(w.lastActorDisplayName),
    progressMinor: null,
    createdInOccurrenceId: w.createdInOccurrenceId,
    meetingId: w.meetingId,
    occurrenceKey: w.occurrenceKey,
    version: w.version,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

function itemsOf(rows: readonly WireItem[]): MeetingItem[] {
  return rows.flatMap((row) => {
    const item = normalizeItem(row);
    return item ? [item] : [];
  });
}

function standaloneItemsOf(rows: readonly WireStandaloneItem[]): MeetingItem[] {
  return rows.flatMap((row) => {
    const item = normalizeStandaloneItem(row);
    return item ? [item] : [];
  });
}

function requiredItem(item: MeetingItem | null, kind: string): MeetingItem {
  if (!item) throw new ApiShapeError(`unknown item kind: ${kind}`);
  return item;
}

/** One item answer from either route family. */
async function itemRequest(
  home: MeetingHome,
  method: 'POST' | 'PATCH',
  path: string,
  json?: unknown,
): Promise<MeetingItem> {
  const options = json === undefined ? {} : { json };
  if (home === null) {
    const wire = await request<WireStandaloneItem>(method, path, options);
    return requiredItem(normalizeStandaloneItem(wire), wire.kind);
  }
  const wire = await request<WireItem>(method, path, options);
  return requiredItem(normalizeItem(wire), wire.kind);
}

export function normalizeItemVersion(w: WireItemVersion): MeetingItemVersion {
  return {
    version: w.version,
    actorUserId: w.actorUserId ?? null,
    body: w.body,
    status: itemStatus(w.status),
    reason: w.reason,
    dueOn: w.dueOn ?? null,
    ownerUserId: w.ownerUserId ?? null,
    createdAt: w.createdAt,
    actorDisplayName: w.actorDisplayName ?? null,
  };
}

export function normalizeRecording(w: WireRecording): MeetingRecording {
  return {
    id: w.id,
    kind: w.kind === 'TRANSCRIPT_TEXT' ? 'TRANSCRIPT_TEXT' : 'URL',
    url: w.url ?? null,
    transcript: w.transcript ?? null,
    addedByUserId: w.addedByUserId ?? null,
    createdAt: w.createdAt,
    occurrenceId: w.occurrenceId,
  };
}

export function normalizeSummary(w: WireSummary): MeetingSummary {
  return {
    id: w.id,
    kind: SUMMARY_KINDS.has(w.kind) ? w.kind : null,
    status: SUMMARY_STATUSES.has(w.status) ? w.status : 'PENDING',
    body: w.body ?? null,
    createdAt: w.createdAt,
    occurrenceId: w.occurrenceId,
    proposedItems: itemsOf(w.proposedItems ?? []),
    failureReason: normalizeAiJobFailureReason(w.failureReason),
    firstPurchaseRequired: w.firstPurchaseRequired ?? false,
    debtMinor: w.debtMinor ?? null,
  };
}

/**
 * A standalone meeting's summary (TMMAPI-0188 U2): no figures and no proposed items (its pending
 * AI items are in the workspace's items, served to the organizer only).
 */
export function normalizeStandaloneSummary(w: WireStandaloneSummary): MeetingSummary {
  return {
    id: w.id,
    kind: SUMMARY_KINDS.has(w.kind) ? w.kind : null,
    status: SUMMARY_STATUSES.has(w.status) ? w.status : 'PENDING',
    body: w.body ?? null,
    createdAt: w.createdAt,
    occurrenceId: w.occurrenceId,
    proposedItems: [],
    failureReason: normalizeAiJobFailureReason(w.failureReason),
    firstPurchaseRequired: w.firstPurchaseRequired ?? false,
    debtMinor: null,
  };
}

export function normalizeStandingSummary(
  w: WireVirtualAccountSummary,
): VirtualAccountStandingSummary {
  return {
    body: w.body,
    authorDisplayName: w.authorDisplayName,
    createdAt: w.createdAt,
    version: w.version,
  };
}

function normalizeOccurrenceView(w: WireOccurrence): MeetingOccurrence {
  return {
    meetingId: w.meetingId,
    virtualAccountId: w.virtualAccountId ?? null,
    occurrenceId: w.occurrenceId ?? null,
    occurrenceKey: w.occurrenceKey,
    startsAt: w.startsAt,
    endsAt: w.endsAt,
    status: occurrenceStatus(w.status),
    heldStartedAt: w.heldStartedAt ?? null,
    heldEndedAt: w.heldEndedAt ?? null,
    priorOccurrenceId: w.priorOccurrenceId ?? null,
  };
}

function normalizeWorkspace(w: WireWorkspace): MeetingWorkspace {
  const since = w.sinceLastMeeting;
  return {
    money: {
      unassignedMinor: w.unassignedMinor,
      balanceMinor: w.balanceMinor,
      envelopes: w.envelopes.flatMap((row) => {
        const envelope = normalizeEnvelope(row);
        return envelope ? [envelope] : [];
      }),
      sinceLastMeeting: {
        since: since.since ?? null,
        priorOccurrenceId: since.priorOccurrenceId ?? null,
        register: since.register.map(normalizeRegisterLine),
        nextCursor: since.nextCursor ?? null,
      },
      lookingAhead: normalizeCashflowPlan(w.lookingAhead),
      summary: w.summary ? normalizeStandingSummary(w.summary) : null,
    },
    agenda: null,
    notes: w.notes.map(normalizeNote),
    items: itemsOf(w.items),
  };
}

/** A standalone occurrence's detail: talk only, so `money` is null (MM-D-150). */
export function normalizeStandaloneOccurrence(
  w: WireStandaloneOccurrenceResponse,
): MeetingOccurrenceDetail {
  const agenda = w.workspace.agenda ?? '';
  return {
    kind: 'STANDALONE',
    occurrence: normalizeOccurrenceView(w.occurrence),
    workspace: {
      money: null,
      agenda: agenda.trim().length > 0 ? agenda : null,
      notes: w.workspace.notes.map(normalizeNote),
      items: standaloneItemsOf(w.workspace.items),
    },
  };
}

function normalizeRegisterLine(w: WireVirtualAccountRegisterLine): VirtualAccountRegisterLine {
  return {
    id: w.id,
    postedOn: w.postedOn,
    payee: w.payee,
    publishedMinor: w.publishedMinor,
    // An allocation to no envelope (Unassigned) carries neither an id nor a name: not listed here.
    allocations: w.allocations.flatMap((a) =>
      a.envelopeId == null && a.envelopeName == null
        ? []
        : [
            {
              envelopeId: a.envelopeId ?? null,
              envelopeName: a.envelopeName ?? null,
              amountMinor: a.amountMinor,
            },
          ],
    ),
  };
}

function parseLocationId(location: string | null): number | null {
  if (location === null) return null;
  const match = location.match(/(\d+)\s*$/);
  if (match === null) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

/** One meeting's route: under its virtual account, or `/v1/me/meetings/{id}` for a standalone one. */
function meetingPath(home: MeetingHome, meetingId: number): string {
  return home === null
    ? `/v1/me/meetings/${meetingId}`
    : `/v1/virtual-accounts/${home}/meetings/${meetingId}`;
}

function occPath(home: MeetingHome, meetingId: number, occurrenceKey: string): string {
  return `${meetingPath(home, meetingId)}/occurrences/${encodeURIComponent(occurrenceKey)}`;
}

const POLL_MS = 2000;
const POLL_MAX_MS = 90_000;

export const meetingsApi = {
  async listForVirtualAccount(
    virtualAccountId: number,
    window?: { from: string; to: string },
  ): Promise<MeetingCalendar> {
    return normalizeCalendar(
      await request<WireCalendar>('GET', `/v1/virtual-accounts/${virtualAccountId}/meetings`, {
        query: window ? { from: window.from, to: window.to } : {},
      }),
    );
  },
  /** Every meeting the caller is in: both kinds (`kind`), standalone ones with no virtual account. */
  async listMine(window: { from: string; to: string }): Promise<MeetingCalendar> {
    return normalizeCalendar(
      await request<WireCalendar>('GET', '/v1/me/meetings', {
        query: { from: window.from, to: window.to },
      }),
    );
  },
  /**
   * The caller's sharing pool (TMMAPI-0188): everyone they share an active virtual account with, in
   * either direction. Names only; a row without an id can't be invited, so it isn't listed.
   */
  async listMeetingPeople(): Promise<PoolPerson[]> {
    const wire = await request<WirePerson[]>('GET', '/v1/me/meeting-people');
    return wire.flatMap((row) =>
      row.userId === undefined ? [] : [{ userId: row.userId, displayName: row.displayName ?? '' }],
    );
  },
  async get(home: MeetingHome, meetingId: number): Promise<Meeting> {
    return normalizeMeeting(await request<WireMeeting>('GET', meetingPath(home, meetingId)));
  },
  /** POST …/meetings — sends only the keys given; an omitted optional key is not set. */
  async create(virtualAccountId: number, body: MeetingCreateRequest): Promise<Meeting> {
    return normalizeMeeting(
      await request<WireMeeting>('POST', `/v1/virtual-accounts/${virtualAccountId}/meetings`, {
        json: body,
      }),
    );
  },
  /**
   * POST /v1/me/meetings without a virtual account: a standalone meeting (talk only). Its invitees
   * come from `listMeetingPeople` (else `422 INVITEE_NOT_IN_POOL`, `details.userId`); a caller who may
   * not schedule one gets `403 MEETING_SCHEDULE_NOT_ALLOWED`.
   */
  async createStandalone(body: Omit<MyMeetingCreateRequest, 'virtualAccountId'>): Promise<Meeting> {
    return normalizeMeeting(await request<WireMeeting>('POST', '/v1/me/meetings', { json: body }));
  },
  /** PATCH …/meetings/{id} — sends only what changed; an omitted key is unchanged. */
  async patch(home: MeetingHome, meetingId: number, body: MeetingPatchRequest): Promise<Meeting> {
    return normalizeMeeting(
      await request<WireMeeting>('PATCH', meetingPath(home, meetingId), { json: body }),
    );
  },
  /** A standalone meeting's agenda, a writer's edit (organizer or not); blank clears it. */
  async putAgenda(meetingId: number, agenda: string): Promise<Meeting> {
    return normalizeMeeting(
      await request<WireMeeting>('PUT', `${meetingPath(null, meetingId)}/agenda`, {
        json: { agenda },
      }),
    );
  },
  async cancelSeries(home: MeetingHome, meetingId: number): Promise<void> {
    await request<void>('DELETE', meetingPath(home, meetingId));
  },
  async endFrom(home: MeetingHome, meetingId: number, fromDate: string): Promise<Meeting> {
    return normalizeMeeting(
      await request<WireMeeting>('POST', `${meetingPath(home, meetingId)}/end-from`, {
        json: { fromDate },
      }),
    );
  },
  async ics(home: MeetingHome, meetingId: number): Promise<DownloadResult> {
    return download(`${meetingPath(home, meetingId)}/ics`, {
      headers: { Accept: 'text/calendar' },
      accept: 'text/calendar',
    });
  },
  async getOccurrence(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<MeetingOccurrenceDetail> {
    if (home === null) {
      return normalizeStandaloneOccurrence(
        await request<WireStandaloneOccurrenceResponse>(
          'GET',
          occPath(null, meetingId, occurrenceKey),
        ),
      );
    }
    const wire = await request<WireOccurrenceResponse>(
      'GET',
      occPath(home, meetingId, occurrenceKey),
    );
    return {
      kind: 'VIRTUAL_ACCOUNT',
      occurrence: normalizeOccurrenceView(wire.occurrence),
      workspace: normalizeWorkspace(wire.workspace),
    };
  },
  async cancelOccurrence(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<void> {
    await request<void>('POST', `${occPath(home, meetingId, occurrenceKey)}/cancel`);
  },
  async start(home: MeetingHome, meetingId: number, occurrenceKey: string): Promise<void> {
    await request<void>('POST', `${occPath(home, meetingId, occurrenceKey)}/start`);
  },
  async end(home: MeetingHome, meetingId: number, occurrenceKey: string): Promise<void> {
    await request<void>('POST', `${occPath(home, meetingId, occurrenceKey)}/end`);
  },
  async listNotes(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<MeetingNote[]> {
    const wire = await request<WireNote[]>(
      'GET',
      `${occPath(home, meetingId, occurrenceKey)}/notes`,
    );
    return wire.map(normalizeNote);
  },
  async addNote(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    body: string,
  ): Promise<MeetingNote> {
    return normalizeNote(
      await request<WireNote>('POST', `${occPath(home, meetingId, occurrenceKey)}/notes`, {
        json: { body },
      }),
    );
  },
  async patchNote(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    noteId: number,
    body: { body: string; version: number },
  ): Promise<MeetingNote> {
    return normalizeNote(
      await request<WireNote>(
        'PATCH',
        `${occPath(home, meetingId, occurrenceKey)}/notes/${noteId}`,
        { json: body },
      ),
    );
  },
  /** A virtual account meeting's note only: a standalone meeting's notes have no delete route. */
  async deleteNote(
    virtualAccountId: number,
    meetingId: number,
    occurrenceKey: string,
    noteId: number,
  ): Promise<void> {
    await request<void>(
      'DELETE',
      `${occPath(virtualAccountId, meetingId, occurrenceKey)}/notes/${noteId}`,
    );
  },
  async listItems(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<MeetingItem[]> {
    const path = `${occPath(home, meetingId, occurrenceKey)}/items`;
    if (home === null) return standaloneItemsOf(await request<WireStandaloneItem[]>('GET', path));
    return itemsOf(await request<WireItem[]>('GET', path));
  },
  async listVirtualAccountItems(
    virtualAccountId: number,
    // `envelopeId` (TMMAPI-0186 U3): only the items about that envelope, filtered server-side.
    query: { kind?: ItemKind; status?: ItemStatus; envelopeId?: number } = {},
  ): Promise<MeetingItem[]> {
    return itemsOf(
      await request<WireItem[]>('GET', `/v1/virtual-accounts/${virtualAccountId}/items`, { query }),
    );
  },
  /** Every item the caller can read, standalone meetings' too (no `virtualAccountId` on those). */
  async listMyItems(status?: ItemStatus): Promise<MeetingItem[]> {
    return itemsOf(
      await request<WireItem[]>('GET', '/v1/me/items', {
        query: status !== undefined ? { status } : {},
      }),
    );
  },
  /** A standalone meeting's item carries no envelope or target: never send them for one. */
  async createItem(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    body: MeetingItemCreateRequest,
  ): Promise<MeetingItem> {
    return itemRequest(home, 'POST', `${occPath(home, meetingId, occurrenceKey)}/items`, body);
  },
  async patchItem(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    itemId: number,
    body: MeetingItemPatchRequest,
  ): Promise<MeetingItem> {
    return itemRequest(
      home,
      'PATCH',
      `${occPath(home, meetingId, occurrenceKey)}/items/${itemId}`,
      body,
    );
  },
  async confirmItem(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    itemId: number,
  ): Promise<MeetingItem> {
    return itemRequest(
      home,
      'POST',
      `${occPath(home, meetingId, occurrenceKey)}/items/${itemId}/confirm`,
    );
  },
  async itemVersions(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    itemId: number,
  ): Promise<MeetingItemVersion[]> {
    const wire = await request<WireItemVersion[]>(
      'GET',
      `${occPath(home, meetingId, occurrenceKey)}/items/${itemId}/versions`,
    );
    return wire.map(normalizeItemVersion);
  },
  async listRecordings(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<MeetingRecording[]> {
    const wire = await request<WireRecording[]>(
      'GET',
      `${occPath(home, meetingId, occurrenceKey)}/recordings`,
    );
    return wire.map(normalizeRecording);
  },
  async addRecording(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    body: { kind: RecordingKind; url?: string; transcript?: string },
  ): Promise<MeetingRecording> {
    return normalizeRecording(
      await request<WireRecording>(
        'POST',
        `${occPath(home, meetingId, occurrenceKey)}/recordings`,
        {
          json: body,
        },
      ),
    );
  },
  async deleteRecording(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    recordingId: number,
  ): Promise<void> {
    await request<void>(
      'DELETE',
      `${occPath(home, meetingId, occurrenceKey)}/recordings/${recordingId}`,
    );
  },
  async listSummaries(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
  ): Promise<MeetingSummary[]> {
    const path = `${occPath(home, meetingId, occurrenceKey)}/summaries`;
    if (home === null) {
      return (await request<WireStandaloneSummary[]>('GET', path)).map(normalizeStandaloneSummary);
    }
    return (await request<WireSummary[]>('GET', path)).map(normalizeSummary);
  },
  async getSummary(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    summaryId: number,
  ): Promise<MeetingSummary> {
    const path = `${occPath(home, meetingId, occurrenceKey)}/summaries/${summaryId}`;
    if (home === null) {
      return normalizeStandaloneSummary(await request<WireStandaloneSummary>('GET', path));
    }
    return normalizeSummary(await request<WireSummary>('GET', path));
  },
  /** A standalone meeting's summary is its organizer's alone, on their own wallet (TMMAPI-0188 U2). */
  async requestSummary(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    kind: SummaryKind,
  ): Promise<MeetingSummary> {
    const result = await requestMeta<WireSummary | WireStandaloneSummary | null>(
      'POST',
      `${occPath(home, meetingId, occurrenceKey)}/summaries`,
      { json: { kind } },
    );
    if (result.data) {
      return home === null
        ? normalizeStandaloneSummary(result.data)
        : normalizeSummary(result.data as WireSummary);
    }
    const id = parseLocationId(result.location);
    if (id !== null) {
      return {
        id,
        kind,
        status: 'PENDING',
        body: null,
        createdAt: null,
        occurrenceId: null,
        proposedItems: [],
        failureReason: null,
        firstPurchaseRequired: false,
        debtMinor: null,
      };
    }
    throw new ApiShapeError('summary response had no id');
  },
  async pollSummary(
    home: MeetingHome,
    meetingId: number,
    occurrenceKey: string,
    summaryId: number,
  ): Promise<MeetingSummary> {
    const start = Date.now();
    let current = await meetingsApi.getSummary(home, meetingId, occurrenceKey, summaryId);
    while (current.status === 'PENDING' && Date.now() - start < POLL_MAX_MS) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, POLL_MS);
      });
      current = await meetingsApi.getSummary(home, meetingId, occurrenceKey, summaryId);
    }
    return current;
  },
  async getVirtualAccountSummary(virtualAccountId: number): Promise<VirtualAccountStandingSummary> {
    return normalizeStandingSummary(
      await request<WireVirtualAccountSummary>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/summary`,
      ),
    );
  },
  /**
   * PUT /v1/virtual-accounts/{id}/summary (TMMAPI-0146): `version` is the one this edit is based on
   * (0 when there is none yet); a stale one answers `409 VERSION_CONFLICT`.
   */
  async putVirtualAccountSummary(
    virtualAccountId: number,
    body: string,
    version: number,
  ): Promise<VirtualAccountStandingSummary> {
    const json: VirtualAccountSummaryPutRequest = { body, version };
    return normalizeStandingSummary(
      await request<WireVirtualAccountSummary>(
        'PUT',
        `/v1/virtual-accounts/${virtualAccountId}/summary`,
        { json },
      ),
    );
  },
  async listVirtualAccountSummaryVersions(
    virtualAccountId: number,
  ): Promise<VirtualAccountStandingSummary[]> {
    const wire = await request<WireVirtualAccountSummary[]>(
      'GET',
      `/v1/virtual-accounts/${virtualAccountId}/summary/versions`,
    );
    return wire.map(normalizeStandingSummary);
  },
};

export type {
  MyMeetingCreateRequest,
  MeetingCreateRequest,
  MeetingPatchRequest,
  MeetingItemCreateRequest,
  MeetingItemPatchRequest,
};
